/* eslint-disable @typescript-eslint/no-explicit-any */

import { P2PComponentsData } from "../../../P2PTrade/types";
import { UseFormReturn } from "react-hook-form";
import { createType } from "..";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Check, ChevronsUpDown, PlusIcon, XIcon } from "lucide-react";
import { Label, textVariants } from "@components/Text/Text";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import { Button } from "@components/Button/Button";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import api from "@/src/utils";
import apiRoutes from "@/src/apiRoutes";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Modal } from "@/src/components/Modal/Modal";
import { PreviewOfferData } from "../../types";
import AddSellPayment from "./AddSellPayment";
import GoBack from "@/src/components/goBack";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TOptions } from "i18next";

interface Props {
  data: P2PComponentsData | undefined;
  form: UseFormReturn<createType>;
  setActive: Dispatch<SetStateAction<number>>;
  back: () => void;
}

export type TPaymentMethods = {
  id: string;
  account_name: string;
  account_number: string;
  extra: string;
};

const Step2 = ({ data, form, setActive, back }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string, options?: TOptions) =>
    t(`app.p2p.create.${key}`, options);
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    TPaymentMethods[]
  >([]);

  const { register, getValues } = form;

  const generatePreview = async () => {
    const res = await api.post(apiRoutes.p2pPreview, {
      ...getValues(),
      type: "generate",
      payment_methods: selectedPaymentMethods,
      currency: getValues().currency.slice(0, 3),
    });
    return res.data;
  };

  const {
    mutate,
    data: previewInfo,
    isLoading: previewLoad,
  } = useMutation<PreviewOfferData>({
    mutationFn: generatePreview,
    onSuccess: (data) => {
      if (data?.success) {
        setShow(true);
      } else {
        toast.warning(data?.info as any);
      }
    },
  });

  const createOffer = async () => {
    const res = await api.post(`${apiRoutes.p2pPreview}${id}/`, {
      type: "save",
    });
    return res.data;
  };

  const { mutate: create, isLoading: createLoad } = useMutation({
    mutationFn: createOffer,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setActive(2);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const transPms = data?.info?.payment_methods.filter((item) =>
    selectedPaymentMethods.some((pm) => pm.id === item?.id.toString())
  );

  function handelPmRemoval(id: string) {
    const newPayments = selectedPaymentMethods.filter((item) => item.id !== id);
    setSelectedPaymentMethods(newPayments);
  }

  useEffect(() => {
    if (form.watch("trans_type")) {
      setSelectedPaymentMethods([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch("trans_type")]);

  return (
    <>
      <GoBack backFn={back} />
      <div className="flex  flex-col md:flex-row-reverse gap-5">
        <div className="w-full">
          <Label>{trans("form.paymentMethod.label")}</Label>
          <div className="bg-white dark:bg-grey-700 h-32 rounded-md p-2">
            {form.watch("trans_type") === "sell" ? (
              <div className="grid">
                <div className="grid gap-1">
                  <span className={textVariants({ size: "xs" })}>
                    {trans("form.paymentMethod.sellText")}
                  </span>
                </div>
                <Button
                  onClick={() => setShowPaymentMethods(true)}
                  className="flex items-center gap-2 mt-3"
                >
                  <PlusIcon className="h-5 w-5 text-white" />{" "}
                  {trans("form.addButton")}
                </Button>
              </div>
            ) : (
              <div>
                <Popover open={open} onOpenChange={setOpen}>
                  <div className=" grid ">
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                          " flex justify-between text-black w-full items-center border-gray-200 bg-input-field rounded-md px-3 py-2.5 text-sm font-semibold shadow-none dark:bg-grey-500 dark:text-white whitespace-nowrap "
                        )}
                      >
                        <span className="text-gray-500">
                          {trans("form.paymentMethod.label")}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className={cn(
                        ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                      )}
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder={`Search payment methods`} />
                        <CommandEmpty>None found.</CommandEmpty>
                        <CommandGroup>
                          {data?.info.payment_methods.map((item, index) => (
                            <CommandItem
                              key={index}
                              onSelect={() => {
                                setOpen(false);
                                if (selectedPaymentMethods.length < 3) {
                                  setSelectedPaymentMethods((old) => [
                                    ...old,
                                    {
                                      id: item?.id.toString(),
                                      account_name: "",
                                      account_number: "",
                                      extra: "",
                                    },
                                  ]);
                                } else {
                                  toast.warning(
                                    "You can only select up to 3 payment methods"
                                  );
                                }
                              }}
                              value={item?.name}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedPaymentMethods.some(
                                    (pm) => pm.id === item?.id.toString()
                                  )
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              <div className="flex items-center h-1">
                                <span className="text-lg mr-2">
                                  <img
                                    src={item?.image}
                                    alt={item?.name}
                                    className="h-6 w-6"
                                  />
                                </span>
                                <span>{item?.name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </div>
                </Popover>
              </div>
            )}

            <div className="py-3 flex  flex-wrap md:flex-row  md:space-x-5">
              {transPms?.map((item) => (
                <div className="flex" key={item?.id}>
                  <span
                    onClick={() => handelPmRemoval(item?.id.toString())}
                    className={cn(
                      textVariants({ size: "sm" }),
                      " bg-input-field dark:bg-grey-500 hover:opacity-40 px-3 py-1 rounded-2xl flex items-center cursor-pointer"
                    )}
                  >
                    <span className="p-[1px] h-4 bg-red-500"></span>
                    <span className="pl-1 whitespace-nowrap">{item?.name}</span>
                    <span className="pl-2">
                      <XIcon className="h-4 w-4" />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full">
          <Label>{trans("form.tradeInstructions.label")}</Label>
          <textarea
            {...register("terms", { required: true })}
            className="bg-input-field dark:bg-grey-600 dark:text-white rounded-md w-full p-2 md:p-4"
            placeholder={trans("form.tradeInstructions.placeholder")}
            cols={30}
            rows={5}
          />
        </div>
      </div>

      <div className="md:mt-10 mt-5 grid ">
        <Button
          onClick={() => mutate()}
          loading={previewLoad}
          // className="px-20"
        >
          {trans("form.previewButton")}
        </Button>
      </div>

      {/* Previw create offer modal  */}
      {previewInfo?.success && (
        <Modal
          open={show}
          setOpen={setShow}
          setter="state"
          title="Offer Preview"
          footer={
            <div className="flex justify-end items-center mt-5 space-x-4">
              <Button onClick={() => setShow(false)} variant={"ghost"}>
                {trans("form.preview.buttons.cancel")}
              </Button>
              <Button onClick={() => create()} loading={createLoad}>
                {trans("form.preview.buttons.submit")}
              </Button>
            </div>
          }
        >
          {
            <div className="grid gap-5">
              <div className="flex justify-between">
                <span className={textVariants({ variant: "desc" })}>
                  {trans("form.preview.body.rate")}
                </span>
                <span className={textVariants({ font: "semibold" })}>
                  {getValues("rate")} {previewInfo?.info?.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={textVariants({ variant: "desc" })}>
                  {trans("form.preview.body.quantity")}
                </span>
                <span className={textVariants({ font: "semibold" })}>
                  {previewInfo?.info?.amount} {previewInfo?.info?.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={textVariants({ variant: "desc" })}>
                  {trans("form.preview.body.limit")}
                </span>
                <span className={textVariants({ font: "semibold" })}>
                  {previewInfo?.info?.min_order_price} -{" "}
                  {previewInfo?.info?.max_order_price}{" "}
                  {previewInfo?.info?.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={textVariants({ variant: "desc" })}>
                  {trans("form.preview.body.paymentMethod")}
                </span>
                <span className={textVariants({ font: "semibold" })}>
                  <div className="grid gap-2">
                    {previewInfo?.info?.payment_methods.map((pm: any) => (
                      <span
                        key={pm}
                        className={cn(
                          textVariants({ size: "sm" }),
                          "border-l-[1.5px]  border-red-500"
                        )}
                      >
                        {" "}
                        <span className="pl-1">{pm}</span>{" "}
                      </span>
                    ))}
                  </div>
                </span>
              </div>
            </div>
          }
        </Modal>
      )}

      <AddSellPayment
        paymentMethods={selectedPaymentMethods}
        setPaymentMethods={setSelectedPaymentMethods}
        data={data}
        open={showPaymentMethods}
        setOpen={setShowPaymentMethods}
      />
    </>
  );
};

export default Step2;
